import { useMemo } from 'react';

import parse from 'html-react-parser';
import { marked } from 'marked';
import { Link, useParams } from 'react-router-dom';

import { ArtifactGroup } from './utils';
import { KimMessageArtifact } from '../../../../../../../../../../backend/src/matter/subset/thread/kim-message-artifact.entity';
import { Permission } from '../../../../../../../../../../backend/src/shared/action-registry';
import { KIM_MARKED_OPTIONS } from '../utils/kim-markdown';
import { useUserContext } from '@/Contexts/User';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';

export function ArtifactCard({
  artifact,
  versions,
  onTogglePublished,
  onDownload,
  onShowVersions,
  onLinkClick,
  togglingKey,
}: {
  artifact: KimMessageArtifact;
  versions?: KimMessageArtifact[];
  onTogglePublished: (params: { artifactId: string; versionNumber: number }) => void;
  onDownload: (artifactId: string, versionNumber: number) => void;
  onShowVersions?: (group: ArtifactGroup) => void;
  onLinkClick?: () => void;
  togglingKey: string | null;
}) {
  const { matterId, subsetId } = useParams();
  const artifactKey = `${artifact.id}-${artifact.versionNumber}`;
  const threadUrl = `/matters/${matterId}/subsets/${subsetId}/new-kim/${artifact.message.threadId}?artifact=${artifactKey}`;
  const versionCount = versions?.length ?? 0;

  const { user } = useUserContext();

  const artifactMarkdown = useMemo(() => {
    const artifactMessage = artifact.message.content.find((m) => m.type === 'artifact');
    const content = artifactMessage?.content
      .filter((m) => m.type === 'text')
      .map((m) => m.value)
      .join('')
      .slice(0, 500);

    if (!content) return null;

    return parse(marked.parse(content, KIM_MARKED_OPTIONS) as string, {
      replace: (node) => {
        if (node.type === 'script') return <></>;
        if (node.type !== 'tag' || !('name' in node)) return undefined;
        if (node.name === 'iframe' || node.name === 'object' || node.name === 'embed') return <></>;
        if (node.attribs) {
          for (const name of Object.keys(node.attribs)) {
            if (name.toLowerCase().startsWith('on')) delete node.attribs[name];
          }
        }
        return undefined;
      },
    });
  }, [artifact.message.content]);

  return (
    <div className="relative flex flex-col rounded-xl border border-gray-200 bg-white transition">
      <Link
        to={threadUrl}
        onClick={() => {
          onLinkClick?.();
        }}
        className="group aspect-[4/3] overflow-hidden rounded-t-xl bg-gray-100/80 px-8 py-13 transition-colors duration-200 hover:bg-gray-200/50"
      >
        <div className="prose prose-sm prose-hr:my-1.5 prose-th:bg-white prose-p:my-1 prose-h1:text-base prose-h1:font-semibold prose-h2:my-1 prose-headings:font-medium prose-headings:leading-tight prose-h1:my-1 prose-h2:text-sm flex flex-col overflow-hidden rounded-lg border bg-white p-6 pt-4 font-serif text-xs tracking-tight [&>*]:pointer-events-none">
          {artifactMarkdown}
        </div>
      </Link>

      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start justify-between p-1.5">
        {versionCount > 1 && onShowVersions && versions ? (
          <Button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onShowVersions({ latest: artifact, versions });
            }}
            variant="white"
            size="sm"
            className="pointer-events-auto"
          >
            View all versions ({versionCount})
          </Button>
        ) : (
          <div className="font-inter pointer-events-auto rounded-md border bg-white px-2 py-1 text-xs text-gray-500">
            Version {artifact.versionNumber}
          </div>
        )}

        {artifact.published && (
          <span className="pointer-events-auto inline-flex items-center gap-1.25 rounded-md border border-teal-200 bg-teal-50 px-2.25 py-1 text-[11px] font-medium text-teal-600">
            <Icon className="lucide--users-round mb-px" />
            <span>Published</span>
          </span>
        )}
      </div>

      <div className="flex flex-col gap-0.5 border-t px-4 py-2.5 pt-1.75 pr-2.5 text-left">
        <div className="flex items-center justify-between gap-2">
          <h3 className="truncate text-sm font-medium text-gray-600">{artifact.name}</h3>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" aria-label="Artifact actions" className="h-6">
                <Icon className="lucide--more-horizontal" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={2}>
              {user?.id === artifact.message.thread.createdById && (
                <PermissionGate
                  permission={Permission.ThreadPublish}
                  uiMode="disabled"
                  disabledMessage="You don't have permission to publish work product"
                >
                  <DropdownMenuItem
                    className="text-xs"
                    onClick={() => {
                      onTogglePublished({ artifactId: artifact.id, versionNumber: artifact.versionNumber });
                    }}
                    disabled={togglingKey === artifactKey}
                  >
                    {artifact.published
                      ? `Unpublish v${artifact.versionNumber}`
                      : `Publish Version ${artifact.versionNumber}`}
                  </DropdownMenuItem>
                </PermissionGate>
              )}
              <PermissionGate
                permission={Permission.ThreadDownload}
                uiMode="disabled"
                disabledMessage="You don't have permission to download an answer"
              >
                <DropdownMenuItem
                  className="text-xs"
                  onClick={() => {
                    onDownload(artifact.id, artifact.versionNumber);
                  }}
                >
                  Download as Word document
                </DropdownMenuItem>
              </PermissionGate>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <p className="text-xs text-gray-400">{new Date(artifact.createdDate).toLocaleDateString()}</p>
      </div>
    </div>
  );
}
